import { Card } from "@/components/ui/card";
import { useState } from "react";
import { PROCESS_STEPS } from "@/lib/process-steps";

export function AnimatedProcessFlow() {
  const [activeStep, setActiveStep] = useState<number | null>(null);

  return (
    <section id="process" className="py-24 px-4 bg-background">
      <div className="container mx-auto max-w-7xl">
        <div className="text-center mb-12 sm:mb-16 space-y-4">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-balance px-4">
            From idea to reality in <span className="text-primary">four simple steps</span>
          </h2>
          <p className="text-lg sm:text-xl text-muted-foreground max-w-2xl mx-auto text-pretty px-4">
            Our streamlined process makes it easy to create professional custom products—no design
            experience needed
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
          {PROCESS_STEPS.map((step, index) => (
            <Card
              key={index}
              className={`p-6 transition-all duration-300 cursor-pointer group relative overflow-hidden ${
                activeStep === index
                  ? "shadow-2xl -translate-y-3 ring-2 ring-primary"
                  : "hover:shadow-xl hover:-translate-y-2"
              }`}
              style={{
                animationDelay: `${index * 0.1}s`,
              }}
              onClick={() => setActiveStep(activeStep === index ? null : index)}
            >
              {/* Step number */}
              <div
                className={`absolute top-4 right-4 text-4xl sm:text-5xl md:text-6xl font-bold transition-colors ${
                  activeStep === index
                    ? "text-primary/20"
                    : "text-muted/10 group-hover:text-muted/20"
                }`}
              >
                {index + 1}
              </div>

              {/* Large decorative icon at bottom - only when another card is expanded */}
              {activeStep !== null && activeStep !== index && (
                <div className="hidden sm:block absolute bottom-4 right-4 pointer-events-none">
                  <step.icon
                    className={`w-16 md:w-20 lg:w-24 h-16 md:h-20 lg:h-24 ${step.color} opacity-10`}
                    strokeWidth={1.5}
                  />
                </div>
              )}

              <div className="relative space-y-4">
                <div
                  className={`w-14 h-14 rounded-xl ${step.bgColor} flex items-center justify-center group-hover:scale-110 transition-transform ${
                    activeStep === index ? "scale-110" : ""
                  }`}
                >
                  <step.icon className={`w-7 h-7 ${step.color}`} />
                </div>

                <h3 className="text-lg sm:text-xl font-bold">{step.title}</h3>
                <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                  {step.description}
                </p>

                <div
                  className={`transition-all duration-300 overflow-hidden ${
                    activeStep === index ? "max-h-48 opacity-100 mt-4" : "max-h-0 opacity-0"
                  }`}
                >
                  <div className="pt-4 border-t border-border space-y-2">
                    {step.details.map((detail, i) => (
                      <div key={i} className="flex items-start gap-2 text-sm">
                        <div className="w-1.5 h-1.5 rounded-full bg-primary mt-1.5 flex-shrink-0" />
                        <span className="text-muted-foreground">{detail}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Arrow connector (hidden on last item) */}
              {index < PROCESS_STEPS.length - 1 && (
                <div className="hidden lg:block absolute top-1/2 -right-3 w-6 h-6 text-primary/30 z-10">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M5 12h14M12 5l7 7-7 7" />
                  </svg>
                </div>
              )}
            </Card>
          ))}
        </div>

        <div className="text-center mt-8 text-sm text-muted-foreground">
          Click any step to see more details
        </div>
      </div>
    </section>
  );
}
